import React, { useState } from 'react'
import { Mail, ArrowRight, Sparkles, Zap, TrendingUp } from 'lucide-react'

const Hero: React.FC = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setIsLoading(true)
    setMessage('')

    try {
      const response = await fetch('https://newsletter-backend.dhkim79.workers.dev/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()
      setMessage(data.message)
      
      if (data.success) {
        setEmail('')
      }
    } catch (error) {
      setMessage('구독 중 오류가 발생했습니다. 다시 시도해주세요.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className="relative bg-gradient-to-br from-primary-50 via-purple-50 to-indigo-100 section-padding overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200/30 to-purple-200/30 rounded-full blur-3xl floating-animation"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-200/30 to-primary-200/30 rounded-full blur-3xl floating-animation" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-purple-200/20 to-primary-200/20 rounded-full blur-3xl floating-animation" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="container-max text-center relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-primary-200 rounded-full px-6 py-3 mb-8 fade-in-up">
            <Sparkles className="w-5 h-5 text-primary-600" />
            <span className="text-sm font-semibold text-primary-700">🚀 AI 트렌드의 최전선</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-black text-gray-900 mb-8 leading-tight fade-in-up">
            AI 최전선의 진짜 트렌드
            <br />
            <span className="gradient-text">5분 핵심 요약</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-700 mb-16 leading-relaxed max-w-3xl mx-auto fade-in-up" style={{animationDelay: '0.2s'}}>
            글로벌 AI 커뮤니티의 최신 인사이트를
            <br />
            <span className="font-semibold text-primary-700">매주 요약 전달합니다</span>
          </p>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 max-w-4xl mx-auto">
            <div className="glass-effect rounded-2xl p-6 fade-in-left" style={{animationDelay: '0.4s'}}>
              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-2">10,000+</div>
              <div className="text-sm text-gray-600">글로벌 게시물 분석</div>
            </div>
            <div className="glass-effect rounded-2xl p-6 fade-in-up" style={{animationDelay: '0.6s'}}>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-2">5분</div>
              <div className="text-sm text-gray-600">핵심 요약 시간</div>
            </div>
            <div className="glass-effect rounded-2xl p-6 fade-in-right" style={{animationDelay: '0.8s'}}>
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-2">매주</div>
              <div className="text-sm text-gray-600">정기 발송</div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="max-w-lg mx-auto fade-in-up" style={{animationDelay: '1s'}}>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일 주소를 입력하세요"
                  className="input-field pl-14 text-lg"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary flex items-center justify-center gap-3 whitespace-nowrap text-lg pulse-glow"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    구독 중...
                  </>
                ) : (
                  <>
                    무료 구독하기
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
            
            {message && (
              <div className={`mt-6 p-4 rounded-xl ${
                message.includes('완료') || message.includes('재활성화') 
                  ? 'bg-green-50 border border-green-200 text-green-700' 
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                <p className="text-sm font-medium">{message}</p>
              </div>
            )}
          </form>

          <p className="text-sm text-gray-500 mt-8 fade-in-up" style={{animationDelay: '1.2s'}}>
            ✨ 구독 취소는 언제든지 가능합니다
          </p>
        </div>
      </div>
    </section>
  )
}

export default Hero
